"use client";

import React from "react";
import { Cpu, PhoneCall, FileCheck, Wrench, Rocket, ArrowRight, Activity, Clock, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: "01",
  label: "LABORATORY ANALYSIS",
  heading: "HPLC/MS Verification",
  description: "Third-party High-Performance Liquid Chromatography and Mass Spectrometry validation for every synthesis batch.",
  icon: FileCheck,
  align: "left",
  colSpan: "md:col-span-5 md:col-start-2"
}, {
  id: "02",
  label: "PURITY STANDARDS",
  heading: "99%+ Synthesis",
  description: "Strict adherence to ultra-high purity benchmarks across all synthesized peptide sequences and lyophilized compounds.",
  icon: Activity,
  align: "right",
  colSpan: "md:col-span-5 md:col-start-7"
}, {
  id: "03",
  label: "STABILITY CONTROL",
  heading: "Thermal Protection",
  description: "Climate-controlled storage and cold-chain logistics protocols to maintain molecular structural integrity during transit.",
  icon: Clock,
  align: "left",
  colSpan: "md:col-span-5 md:col-start-2"
}, {
  id: "04",
  label: "DATA TRANSPARENCY",
  heading: "Batch Documentation",
  description: "Comprehensive audit trails and digital COA records accessible for every catalog item via our secure research portal.",
  icon: Cpu,
  align: "right",
  colSpan: "md:col-span-5 md:col-start-7"
}, {
  id: "05",
  label: "QUALITY RELEASE",
  heading: "Final Compliance",
  description: "Rigorous quality assurance sign-off prior to secure, vacuum-sealed distribution to global research facilities.",
  icon: Rocket,
  align: "center",
  colSpan: "md:col-span-8 md:col-start-3"
}];
const protocolFeatures = [{
  title: "Cold-Chain Logistics",
  description: "Temperature-monitored shipping ensuring maximum peptide potency.",
  icon: Clock
}, {
  title: "Third-Party Tested",
  description: "Independent lab verification for identity, purity, and concentration.",
  icon: FileCheck
}, {
  title: "Purity Benchmarks",
  description: "Industry-leading standards for research-grade chemical compounds.",
  icon: TrendingUp
}];
function StepCard({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div data-section-id="159520" data-index={index} ref={ref} className={`
        w-full md:w-[calc(33.33%-1rem)] min-w-[300px] flex-grow
        transition-all duration-1000 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
      `}>
      <Card className="bg-card border-border shadow-sm overflow-hidden relative group hover:border-primary/20 transition-colors duration-500 rounded-none h-full">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
        
        <CardHeader className="pb-4 relative z-10">
          <div className="flex items-center justify-between mb-4">
            <Badge variant="outline" className="font-mono text-primary border-primary/20 bg-primary/5 tracking-widest rounded-none">
              STD.{step.id}
            </Badge>
            <step.icon className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors duration-300" />
          </div>
          <div className="space-y-1">
            <p className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] uppercase">
              {step.label}
            </p>
            <CardTitle className="text-2xl font-medium tracking-tight text-foreground">
              {step.heading}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="relative z-10">
          <CardDescription className="font-mono text-sm leading-relaxed text-muted-foreground/90">
            {step.description}
          </CardDescription>
        </CardContent>
      </Card>
    </div>;
}
export default function HowItWorks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: protocolRef,
    isVisible: isProtocolVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="verifiedresearchstandards" className="bg-background py-20 md:py-32 relative overflow-hidden">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-muted/50 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Badge variant="secondary" className="mb-6 font-mono tracking-widest uppercase bg-muted text-foreground border-border rounded-none px-4">
            Quality Protocol
          </Badge>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-6 text-foreground"></h2>
          
        </div>

        <div className="relative flex flex-wrap justify-center gap-4 mb-20">
          {steps.map((step, index) => <StepCard key={step.id} step={step} index={index} />)}
        </div>

        <div ref={protocolRef} className={`transition-all duration-1000 delay-300 ${isProtocolVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <Separator className="bg-border mb-12" />
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 space-y-6">
              <div>
                <h3 className="text-lg font-bold tracking-tight text-foreground mb-2">
                  Research Compliance
                </h3>
                <p className="font-mono text-sm text-muted-foreground">
                  Engineered exclusively for Signal Source One laboratory partners.
                </p>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Our systems are built with a singular focus: ensuring chemical integrity from synthesis to delivery. We don't just provide peptides; we engineer clinical-grade reliability for your research.
              </p>
              <Button asChild size="lg" className="w-full sm:w-auto group rounded-none bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="/ShopPage">
                  Shop Research Catalog
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
            
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
              {protocolFeatures.map((feature, index) => <div key={index} data-index={index} className="space-y-4">
                  <div className="w-10 h-10 rounded-none bg-muted border border-border flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h4 className="font-bold text-foreground">{feature.title}</h4>
                  <p className="font-mono text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>)}
            </div>
          </div>
          
          <Separator className="bg-border mt-12" />
        </div>

      </div>
    </section>;
}